import { FieldLabel } from "./parts";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";

const DISC = {
  running: {
    label: "Running",
    thresholdLabel: "Threshold pace",
    thresholdPh: "e.g. 4:15/km",
    recentLabel: "Recent race result",
    recentPh: "e.g. 10k in 44:30",
    capLabel: "How long can you run non-stop right now?",
    capOptions: ["< 10 min", "10–20 min", "20–40 min", "40–60 min", "60+ min"],
  },
  cycling: {
    label: "Cycling",
    thresholdLabel: "FTP",
    thresholdPh: "e.g. 250 W",
    recentLabel: "Recent test / effort",
    recentPh: "e.g. 20-min avg 265 W",
    capLabel: "How long can you ride comfortably?",
    capOptions: ["< 30 min", "30–60 min", "1–2 h", "2–3 h", "3 h+"],
  },
  swimming: {
    label: "Swimming",
    thresholdLabel: "CSS / threshold pace",
    thresholdPh: "e.g. 1:45 /100m",
    recentLabel: "Recent swim result",
    recentPh: "e.g. 400m in 6:30",
    capLabel: "How far can you swim non-stop?",
    capOptions: ["< 100 m", "100–400 m", "400–1000 m", "1–2 km", "2 km+"],
  },
  strength: { label: "Strength", simple: true },
};

export function metricsValid(level, disciplines, metrics) {
  const advanced = ["Intermediate", "Advanced / Competitive"].includes(level);
  if (!advanced) return true;
  return disciplines.every((d) => {
    if (d === "strength") return true;
    const m = metrics[d] || {};
    return m.not_sure || (m.threshold && m.threshold.trim()) || (m.recent && m.recent.trim());
  });
}

function DisciplineBlock({ dkey, level, m, update }) {
  const cfg = DISC[dkey] || { label: dkey };
  const advanced = ["Intermediate", "Advanced / Competitive"].includes(level);

  if (cfg.simple) {
    return (
      <div className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid={`metrics-${dkey}`}>
        <p className="font-heading font-bold text-foreground">{cfg.label}</p>
        <Input data-testid={`metrics-${dkey}-note`} value={m.note || ""} onChange={(e) => update("note", e.target.value)} placeholder="Key lifts or experience (e.g. squat 100kg, 2 yrs)" />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid={`metrics-${dkey}`}>
      <p className="font-heading font-bold text-foreground">{cfg.label}</p>

      {advanced ? (
        <>
          <div>
            <FieldLabel>{cfg.thresholdLabel} <span className="text-muted-foreground font-normal">(if you know it)</span></FieldLabel>
            <Input data-testid={`metrics-${dkey}-threshold`} value={m.threshold || ""} disabled={m.not_sure} onChange={(e) => update("threshold", e.target.value)} placeholder={cfg.thresholdPh} />
          </div>
          <div>
            <FieldLabel>{cfg.recentLabel} <span className="text-muted-foreground font-normal">(so I can estimate your zones)</span></FieldLabel>
            <Input data-testid={`metrics-${dkey}-recent`} value={m.recent || ""} disabled={m.not_sure} onChange={(e) => update("recent", e.target.value)} placeholder={cfg.recentPh} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">I know my training zones</span>
            <Switch data-testid={`metrics-${dkey}-zones`} checked={!!m.zones_known} onCheckedChange={(v) => update("zones_known", v)} />
          </div>
          <div className="flex items-center justify-between border-t border-border pt-3">
            <span className="text-sm text-muted-foreground">I don't know these yet</span>
            <Switch data-testid={`metrics-${dkey}-notsure`} checked={!!m.not_sure} onCheckedChange={(v) => update("not_sure", v)} />
          </div>
          {m.not_sure && (
            <p className="text-xs text-muted-foreground bg-muted/60 rounded-lg p-3" data-testid={`metrics-${dkey}-testnote`}>
              No problem — I'll start with a controlled, conservative estimate and suggest a short test once you're ready, so your zones are dialled in.
            </p>
          )}
        </>
      ) : (
        <>
          <FieldLabel>{cfg.capLabel}</FieldLabel>
          <div className="flex flex-wrap gap-2" data-testid={`metrics-${dkey}-capacity`}>
            {cfg.capOptions.map((o) => (
              <button key={o} data-testid={`metrics-${dkey}-cap-${o}`} onClick={() => update("capacity", o)}
                className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${m.capacity === o ? "border-primary bg-primary/10 text-foreground" : "border-border bg-background hover:border-primary/50"}`}>
                {o}
              </button>
            ))}
          </div>
          <div>
            <FieldLabel>Any recent result? <span className="text-muted-foreground font-normal">(optional)</span></FieldLabel>
            <Input data-testid={`metrics-${dkey}-recent`} value={m.recent || ""} onChange={(e) => update("recent", e.target.value)} placeholder={cfg.recentPh} />
          </div>
        </>
      )}
    </div>
  );
}

export default function MetricsSection({ disciplines, level, value = {}, onChange }) {
  const update = (dkey, field, v) => {
    onChange({ ...value, [dkey]: { ...(value[dkey] || {}), [field]: v } });
  };
  return (
    <div className="space-y-3">
      {disciplines.map((d) => (
        <DisciplineBlock key={d} dkey={d} level={level} m={value[d] || {}} update={(f, v) => update(d, f, v)} />
      ))}
    </div>
  );
}
